import {useEffect, useRef} from "react";
import {type InjectedFormProps, reduxForm} from "redux-form";
import {DialogComponent, Modal} from "widgets";
import {useOverlay} from "widgets/hooks";

import {type ISelectOption} from "../../../app-utils/form.js";
import {type IConfiguredAttribute} from "../../../app-utils/playbook.js";
import {getAttributeLabel} from "../../../redux/modules/sample-admin.js";
import {ATTRIBUTE_TYPE, ATTRIBUTE_TYPE_OPTIONS, COLUMN_SET, WORKFLOW_PARAM_USAGE,} from "../../../settings/settings.js";
import CheckboxField, {CheckboxReduxFormField} from "../../core/redux-form/CheckboxField.js";
import InputField, {InputReduxFormField} from "../../core/redux-form/InputField.js";
import ReactSelectField, {ReactSelectReduxFormField} from "../../core/redux-form/ReactSelectField.js";

interface IAttributeInputConfigurationDialogOwnProps {
  attribute: IConfiguredAttribute;
  allAttributes: IConfiguredAttribute[];
  onApply?: (attribute: IConfiguredAttribute) => void;
  onCancel?: () => void;
}

export interface IAttributeInputConfigurationDialogProps
  extends
    IAttributeInputConfigurationDialogOwnProps,
    InjectedFormProps<AttributeInputConfigurationDialogFormData, IAttributeInputConfigurationDialogOwnProps> {}

enum FieldName {
  Description = "DESCRIPTION",
  Mandatory = "MANDATORY",
  Label = "LABEL",
  Type = "TYPE",
}

interface AttributeInputConfigurationDialogFormData {
  [FieldName.Description]?: string;
  [FieldName.Label]?: string;
  [FieldName.Type]?: ISelectOption | null;
  [FieldName.Mandatory]?: boolean;
}

const FORM_NAME = "ATTRIBUTE_INPUT_CONFIGURATION";

const AttributeInputConfigurationDialogImpl = (props: IAttributeInputConfigurationDialogProps) => {
  const { overlayProps, state } = useOverlay({
    isOpen: !!props.attribute,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        props.onCancel?.();
      }
    },
  });

  const { attribute, initialize } = props;

  const initializedAttributeId = useRef<number | string | undefined>(undefined);

  useEffect(() => {
    if (initializedAttributeId.current === attribute.id) {
      return;
    }

    initializedAttributeId.current = attribute.id;

    initialize({
      [FieldName.Description]: attribute.description ?? undefined,
      [FieldName.Label]: attribute.label ?? undefined,
      [FieldName.Type]: ATTRIBUTE_TYPE_OPTIONS.find(
        (option: ISelectOption) => attribute.correctedTypeId === option.value,
      ),
      [FieldName.Mandatory]: !!(attribute.mandatory || attribute.correctedMandatory),
    });
  }, [attribute, initialize]);

  const handleSubmitData = (values: AttributeInputConfigurationDialogFormData) => {
    const attribute: IConfiguredAttribute = {
      ...props.attribute,
      description: values[FieldName.Description],
      label: values[FieldName.Label],
      correctedTypeId: values[FieldName.Type]?.value,
      correctedMandatory: values[FieldName.Mandatory],
    };
    props.onApply?.(attribute);
  };

  const validateLabelDuplicates = (value?: string) => {
    if (value == null || value === "") {
      return undefined;
    }

    const duplicateLabelExists = props.allAttributes
      .filter((attribute: IConfiguredAttribute) => attribute.id !== props.attribute.id)
      .find((attribute: IConfiguredAttribute) => {
        const attributeLabel: string = attribute.label || attribute.attributeName;
        return value === attributeLabel && attribute.groupName === props.attribute.groupName;
      });

    if (duplicateLabelExists) {
      return "This label already exist within the same group";
    }

    return undefined;
  };

  const showLabelConfiguration =
    props.attribute.isWorkflowParam || props.attribute.columnSet.id === COLUMN_SET.COS_PARENT_ID.id;
  const showMandatoryCheckbox = !props.attribute.isWorkflowParam;
  const showTypeCorrection =
    props.attribute.isWorkflowParam &&
    props.attribute.attributeType.id !== ATTRIBUTE_TYPE.CONTROLLED_TEXT &&
    props.attribute.attributeType.id !== ATTRIBUTE_TYPE.BOOLEAN &&
    !(props.attribute.usage && props.attribute.usage in WORKFLOW_PARAM_USAGE);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        closeFn={state.close}
        mainButton={{
          label: "Apply",
          onPress: props.handleSubmit(handleSubmitData),
          isLoading: props.submitting,
          isDisabled: props.submitting,
        }}
        title={getAttributeLabel(props.attribute)}
        subtitle="Playbook-specific attribute settings"
        errorCallout={props.error ? { message: props.error } : undefined}
      >
        <div>
          {/* sample level attributes are only configurable with respect to mandatoriness
          (grid display, therefore no label or description) */}
          {showLabelConfiguration && (
            <>
              <InputReduxFormField
                tooltip={`Provide a custom label during data input`}
                componentClass="input"
                name={FieldName.Label}
                label={"Label"}
                placeholder={`Custom label`}
                component={InputField}
                validate={validateLabelDuplicates}
              />

              <InputReduxFormField
                tooltip={`Provide a description or hint during data input`}
                componentClass="input"
                name={FieldName.Description}
                label={"Description"}
                placeholder={`Optional Description`}
                component={InputField}
              />
            </>
          )}
          {showMandatoryCheckbox && (
            <CheckboxReduxFormField
              name={FieldName.Mandatory}
              label="Mandatory"
              component={CheckboxField}
              tooltipText={
                props.attribute.mandatory
                  ? "This attribute is mandatory by default"
                  : "Check to make this attribute mandatory in this playbook"
              }
              disabled={props.attribute.mandatory}
            />
          )}
          {/* do not provide the input type correction for option lists and for everything with a known usage, like paths and files */}
          {showTypeCorrection && (
            <ReactSelectReduxFormField
              name={FieldName.Type}
              isClearable={true}
              isSearchable={true}
              label={"Corrected input type"}
              tooltip={"Use a different input field type..."}
              placeholder={"Select field type..."}
              component={ReactSelectField}
              options={ATTRIBUTE_TYPE_OPTIONS.filter(
                (typeOption: ISelectOption) =>
                  typeOption.value === ATTRIBUTE_TYPE.FREE_TEXT || typeOption.value === ATTRIBUTE_TYPE.FREE_NUMERICAL,
              )}
            />
          )}
        </div>
      </DialogComponent>
    </Modal>
  );
};

const AttributeInputConfigurationDialog = reduxForm<
  AttributeInputConfigurationDialogFormData,
  IAttributeInputConfigurationDialogOwnProps
>({
  form: FORM_NAME,
})(AttributeInputConfigurationDialogImpl);

export default AttributeInputConfigurationDialog;
